import { Injectable, OnDestroy } from '@angular/core';
import { KsThemeOptions, KsThemeVariable } from '@urbannex/storethemes';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { IframeCommunicationService } from './iframe-communication.service';
import { take, takeUntil } from 'rxjs/operators';

export interface PageCSSDeclaration{
  backgroundBasicColor1?: string,
  backgroundBasicColor2?: string,
  backgroundBasicColor3?: string,
  backgroundBasicColor4?: string,
  backgroundAlternativeColor1?: string,
  backgroundAlternativeColor2?: string,
  backgroundAlternativeColor3?:string,
  backgroundAlternativeColor4?: string,
  backgroundPrimaryColor1?: string,
  backgroundPrimaryColor2?: string,
  backgroundPrimaryColor3?:string,
  backgroundPrimaryColor4?: string,
}

export interface ThemeVariables {
  name?:string;
  variables?:KsThemeVariable[];
}

export interface CssValue{
  [key: string]: string
}

@Injectable({
  providedIn: 'root'
})
export class CssServicesService implements OnDestroy{
  colorSubscription:Subscription;
  borderSubscription:Subscription;
  backgrounds$:Subscription;
  colorScheme$:Subscription;
  txtColor$:Subscription;
  themeVariables$:Subscription;

  public pageCSS:BehaviorSubject<PageCSSDeclaration> = new BehaviorSubject<PageCSSDeclaration>({});
  private currentPageBg:Subject<string> = new BehaviorSubject<string>("#fff");
  private borderColors:Subject<CssValue> = new BehaviorSubject<CssValue>({});
  private bgColors:BehaviorSubject<KsThemeVariable[]> = new BehaviorSubject<KsThemeVariable[]>([]);
  private pmColors:BehaviorSubject<KsThemeVariable[]> = new BehaviorSubject<KsThemeVariable[]>([])
  private textColor:Subject<string> = new BehaviorSubject<string>("#222B45");
  private themeVariables:BehaviorSubject<ThemeVariables> = new BehaviorSubject<ThemeVariables>({})

  private destroy$: Subject<void> = new Subject<void>();

  constructor(private communicator:IframeCommunicationService,) { }

  init(){
    this.themeVariables$ = this.communicator.receive("themeVariables").pipe(takeUntil(this.destroy$))
    .subscribe(data => {
      console.log(data);
      let themeOptions = data.data;
      let _themeVariables:ThemeVariables = {};
      _themeVariables.name = themeOptions.name;
      _themeVariables.variables = themeOptions.variables;
      console.log(_themeVariables);
      this.themeVariables.next(_themeVariables);
    })
    this.colorSubscription = this.communicator.receive("css").pipe(takeUntil(this.destroy$))
    .subscribe(data => {
      //console.log(data)
      this.setPageCSS(data["data"]['options']);
      this.setCurrentPageBg(data["data"]['current'])
    });
    this.borderSubscription = this.communicator.receive("borders").pipe(takeUntil(this.destroy$))
    .subscribe(data => {
      this.setBorderColors(data["data"]['options']);
    });
    this.backgrounds$ = this.communicator.receive('backgrounds').pipe(takeUntil(this.destroy$))
    .subscribe(payload => {
      let backgrounds = payload.data.options;
      this.setBgColors(backgrounds);
      //console.log(backgrounds)
    })
    this.colorScheme$ = this.communicator.receive('colorScheme').pipe(take(1))
    .subscribe(payload => {
      let colorScheme = payload["data"].options
      this.setColorScheme(colorScheme);
    })
    this.txtColor$ = this.communicator.receive('txtColor').pipe(takeUntil(this.destroy$))
    .subscribe(payload => {
      let txtColor = payload.data;
      this.setTextColor(txtColor);
    })
  }

  getThemeVariables():Observable<ThemeVariables>{
    return this.themeVariables.asObservable();
  }

  setPageCSS(pageCss:PageCSSDeclaration){
    console.log(pageCss);
    this.pageCSS.next(pageCss);
  }

  setTextColor(color:string){
    this.textColor.next(color);
  }

  getTextColor():Observable<string>{
    return this.textColor.asObservable();
  }

  getPageCSS():Observable<PageCSSDeclaration>{
    console.log("Getting called");
    return this.pageCSS.asObservable()
  }

  setCurrentPageBg(color:string){
    this.currentPageBg.next(color);
  }

  getCurrentPageBg():Observable<string>{
    return this.currentPageBg.asObservable();
  }

  setBorderColors(colorObjects:any){
    //console.log(colorObjects)
    this.borderColors.next(colorObjects)
  }

  getBorderColors():Observable<CssValue>{
    return this.borderColors.asObservable();
  }

  setBgColors(bgColorObj:any){
    let _bgColors:KsThemeVariable[] = [];
    for (let [k,v] of Object.entries(bgColorObj)) {
        //console.log(k+" : "+v)
        let prop:string = <string>k;
        let val:string = <string>v;
        _bgColors.push({prop,val});
      }
    console.log(_bgColors);
    this.bgColors.next(_bgColors);
      /* this.bgBasic = this._bgColors.splice(0,4)
      this.bgPrimary = this._bgColors.splice(0,4)
      this.bgAlt = this._bgColors.splice(0,4) */
    //})
  }
  setColorScheme(pmColorObj:any){
    let _pmColors:KsThemeVariable[] = [];
    for (let [k,v] of Object.entries(pmColorObj)) {
      //console.log(k+" : "+v)
      let prop:string = <string>k;
      let val:string = <string>v;
      _pmColors.push({prop,val});
    }
  this.pmColors.next(_pmColors);
  }

  getBgColors():Observable<KsThemeVariable[]>{
    return this.bgColors.asObservable()
  }

  getPmColors():Observable<KsThemeVariable[]>{
    return this.pmColors.asObservable()
  }
  /**
   * 
   * @returns An array of colors that are merged from the background colors and primary colors.
   * The first 4 colors are basic background colors, the next 4 are primary background colors, and the last 4 are alternative background colors.
   * The order of the colors is as follows:
   * - Basic Background Colors: bgBasic[0-3]
   * - Primary Background Colors: bgPrimary[0-3]
   * - Alternative Background Colors: bgAlt[0-3]
   */
  getMergredColors():string[]{
    let bgBasic:string[]=[];
    let bgPrimary:string[]=[];
    let bgAlt:string[]=[];
    let primaryColors:string[]=[];
    let colors:string[] = [];
    this.getBgColors().pipe(take(1)).subscribe((colorKV:any[]) => {
      //console.log(colorKV);
      colorKV.forEach((colorObj,i) => {
        if(i < 4){
          bgBasic.push(colorObj.val)        
        }else if(i > 3 && i < 8){
          bgPrimary.push(colorObj.val)
        }else if(i > 7){
          bgAlt.push(colorObj.val)
        }

      })
      this.getPmColors().pipe(take(1))
        .subscribe((colorKV:any[]) => {
        //console.log(colorKV)
        colorKV.forEach((colorObj) => {
          primaryColors.push(colorObj.val)
      })
      colors = bgBasic.concat(primaryColors).concat(bgAlt);
      })
    });
    return colors
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
